import { DatePipe } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  ChangeDetectorRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { InputsService } from '@app/inputs/services/inputs.service';
import { TipService } from '@app/onboarding/services/tip.service';
import { UserOutcomeDetail } from '@app/outcomes/outcomes-api.model';

// TODO: Move this from achievements
import { AuthService } from '@app/shared/services/auth.service';
import { HTTP_URL_PATTERN } from '@app/shared/utils/form-helpers';
import { WindowToken } from '@app/shared/window.token';
import { TagsApi } from '@app/tags/tag-api.model';
import { UploadFileResponse } from '@app/uploader/uploader-api.model';
import { UserContentModalBaseDirective } from '@app/user-content/user-input/user-content-modal-base/user-content-modal-base.directive';
import { UserOutcomeImageUploadAdapter } from '@app/user-content/services/adapters/user-outcome-image-upload.adapter';
import {
  DfFormFieldBuilder,
  DfFormFieldConfig,
  DfTemplateOptions,
} from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { UserImageFieldComponent } from '@app/form-fields/wrappers/user-image-field/user-image-field.component';
import {
  MonthPickerFieldComponent,
  MonthPickerFieldParams,
} from '@app/form-fields/wrappers/month-picker-field/month-picker-field.component';

export interface AwardFormModel {
  title: string;
  issuer: string;
  issuerUrl: string;
  dateGroup: {
    issueDate: Date;
  };
  tags: TagsApi.Tag[];
  imageUrl?: string;
}

@Component({
    selector: 'dgx-award-modal',
    templateUrl: './award-modal.component.html',
    styleUrls: ['./award-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AwardModalComponent extends UserContentModalBaseDirective<
  UserOutcomeDetail,
  AwardFormModel
> {
  public readonly i18n = this.translate.instant([
    'dgUserOutcomeEditForm_WhenDidYouReceiveAward',
    'dgUserOutcomeEditForm_UploadAnImage',
    'TagsCtrl_SkillsPlaceholder',
    'dgUserOutcomeEditForm_SaveToProfile',
    'AwardFormCtrl_AwardDate',
    'Core_ValidUrl',
    'Core_SelectDate',
  ]);

  @ViewChild('skillsView')
  public skillsViewRef: TemplateRef<any>;

  constructor(
    @Inject(WindowToken) windowRef: Window,
    cdr: ChangeDetectorRef,
    activeModal: NgbActiveModal,
    authService: AuthService,
    tipService: TipService,
    inputsService: InputsService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private builder: DfFormFieldBuilder,
    private uploadAdapter: UserOutcomeImageUploadAdapter
  ) {
    super(
      windowRef,
      cdr,
      activeModal,
      authService,
      tipService,
      inputsService,
      translate.instant('dgUserOutcomeEditForm_AddAward'),
      translate.instant('dgUserOutcomeEditForm_EditAward')
    );
  }

  public onTagsChange(items: any[], formControl: FormControl) {
    UserContentModalBaseDirective.setCustomFieldValue(formControl, items);
  }

  protected buildFormFields(): DfFormFieldConfig<DfTemplateOptions>[] {
    const urlValidation = {
      urlValidation: {
        expression: (control: AbstractControl) => {
          const valid = !control.value || HTTP_URL_PATTERN.test(control.value);
          return valid;
        },
        message: this.i18n.Core_ValidUrl,
      },
    };

    const shouldHideImageField =
      this.authUser.isRestrictedProfile ||
      !this.authUser.defaultOrgInfo?.settings?.allowRestrictedImages;
    return [
      this.builder
        .title()
        .labeled('dgUserOutcomeEditForm_TitleOfAward') // AwardFormCtrl_AwardTitle
        .withDgatId('awardForm-8ca')
        .autofocused()
        .build(),
      this.builder
        .requiredTextInput('issuer', 'dgUserOutcomeEditForm_IssuedBy')
        .withDgatId('awardForm-51e')
        .build(),
      this.builder
        .optionalTextInput('issuerUrl', 'dgUserOutcomeEditForm_IssuerUrl')
        .ofType('url')
        .withDgatId('awardForm-11f')
        .validatedByIndexed(urlValidation)
        .build(),
      this.builder
        .fieldGroup(
          'dateGroup',
          [
            this.builder
              .foreignField<MonthPickerFieldParams>(
                'issueDate',
                this.i18n.dgUserOutcomeEditForm_WhenDidYouReceiveAward,
                MonthPickerFieldComponent.REGISTERED_FIELD_TYPE,
                {
                  isMaxDateToday: true,
                  ariaLabel: this.i18n.Core_SelectDate,
                }
              )
              .styledBy('first-col df-form__col-half')
              .asRequired()
              .withErrorMessages({
                // auto-generated 'required' message doesn't make sense with this field name
                required: this.i18n.AwardFormCtrl_AwardDate,
              })
              .build(),
          ],
          'df-form__row'
        )
        .build(),
      this.builder
        .foreignField(
          'imageUrl',
          'dgUserOutcomeEditForm_UploadAnImage',
          UserImageFieldComponent.REGISTERED_FIELD_TYPE,
          {
            isHostImageUrl: true, // Exceptionally, outcome images are currently served from the degreed domain instead of blob storage
            uploadAdapter: this.uploadAdapter,
            provideControlValue: (response: UploadFileResponse) => response.url,
          }
        )
        .hiddenWhen(() => shouldHideImageField)
        .build(),
      this.builder
        .customField('tags', 'Core_Skills', this.skillsViewRef, {
          topTags: this.userInterests,
        })
        .unwrapped() // The tag editor has its own label and doesn't require validation, so present it sans fresco's default field wrapper
        .build(),
    ];
  }

  protected initFormModel(source: Partial<UserOutcomeDetail>) {
    this.model = {
      title: source.title,
      issuer: source.source,
      issuerUrl: source.url,
      imageUrl: source.imageUrl,
      dateGroup: {
        issueDate: source.startDate ? new Date(source.startDate) : undefined, // ensure we get a Date, not string
      },
      tags: source.tags ?? [],
    };
  }

  protected createResult(
    model: Partial<AwardFormModel>,
    defaults: Partial<UserOutcomeDetail>
  ): UserOutcomeDetail {
    const result: Partial<UserOutcomeDetail> = {
      ...defaults,
      contentTypeId: 'Award',
      title: model.title,
      source: model.issuer,
      url: model.issuerUrl,
      imageUrl: model.imageUrl,
      startDate: this.datePipe.transform(
        model.dateGroup.issueDate,
        'yyyy-MM-dd'
      ),
      tags: model.tags,
    };
    return result as UserOutcomeDetail;
  }
}
